'use client'

import { useState, useTransition, useEffect } from 'react'
import { Star, Flag } from 'lucide-react'
import { getCompanyReviews, respondToReview, reportReview } from '../_actions/review-actions'
import type { ReviewData } from '../_actions/review-actions'

// D6.QW.1 - reasons shared with comment reports (REPORT_COMMENT_REASONS).
const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'misinformation', label: 'Misinformation / false claims' },
  { value: 'other', label: 'Other' },
] as const

type ReportReason = (typeof REPORT_REASONS)[number]['value']

interface CompanyReviewsProps {
  companyId: string
  isOwner: boolean
  /**
   * Auth user id of the viewer (undefined when unauthenticated).
   * Used to hide the "Report" button on the viewer's own review.
   * The server action also rejects self-reports with FORBIDDEN -
   * this is just to avoid the no-op click round-trip.
   */
  currentUserId?: string
}

export function CompanyReviews({ companyId, isOwner, currentUserId }: CompanyReviewsProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [reviews, setReviews] = useState<ReviewData[]>([])
  const [averageRating, setAverageRating] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()
  const [loaded, setLoaded] = useState(false)
  const [respondingTo, setRespondingTo] = useState<string | null>(null)
  const [responseText, setResponseText] = useState('')
  // D6.QW.1 - report-this-review inline form state
  const [reportingId, setReportingId] = useState<string | null>(null)
  const [reportReason, setReportReason] = useState<ReportReason>('spam')
  const [reportDescription, setReportDescription] = useState('')
  const [reportFeedback, setReportFeedback] = useState<string | null>(null)

  useEffect(() => {
    startTransition(async () => {
      const result = await getCompanyReviews(companyId)
      if (result.success && result.data) {
        setReviews(result.data.reviews)
        setAverageRating(result.data.averageRating)
        setTotalCount(result.data.totalCount)
        setNextCursor(result.data.nextCursor)
      }
      setLoaded(true)
    })
  }, [companyId])

  const handleLoadMore = () => {
    if (!nextCursor || isPending) return
    startTransition(async () => {
      const result = await getCompanyReviews(companyId, nextCursor)
      if (result.success && result.data) {
        setReviews((prev) => [...prev, ...result.data!.reviews])
        setNextCursor(result.data.nextCursor)
      }
    })
  }

  const handleRespond = (reviewId: string) => {
    if (responseText.trim().length < 20) return
    startTransition(async () => {
      const result = await respondToReview(reviewId, responseText.trim())
      if (result.success) {
        setReviews((prev) =>
          prev.map((r) =>
            r.id === reviewId
              ? { ...r, responseContent: responseText.trim(), responseAt: new Date().toISOString() }
              : r,
          ),
        )
        setRespondingTo(null)
        setResponseText('')
      }
    })
  }

  // D6.QW.1 - submit a content_reports row for this review.
  const handleReportSubmit = (reviewId: string) => {
    startTransition(async () => {
      const result = await reportReview(
        reviewId,
        reportReason,
        reportDescription.trim() || undefined,
      )
      if (result.success) {
        setReportFeedback('Thanks - moderators will review this report.')
        setReportingId(null)
        setReportReason('spam')
        setReportDescription('')
        setTimeout(() => setReportFeedback(null), 4000)
      } else {
        setReportFeedback(result.error ?? 'Failed to submit report.')
      }
    })
  }

  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className="h-3.5 w-3.5"
          style={{
            color: star <= Math.round(rating) ? 'var(--color-accent)' : 'var(--color-text-muted)',
            fill: star <= Math.round(rating) ? 'var(--color-accent)' : 'none',
          }}
        />
      ))}
    </div>
  )

  if (!loaded || totalCount === 0) return null

  return (
    <div className="mt-8 border-t border-[var(--color-border-default)] pt-8">
      <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]" style={font}>
        Reviews
      </h2>

      {/* Summary */}
      {totalCount > 0 && (
        <div className="mb-6 flex items-center gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--color-text-primary)]" style={font}>
              {averageRating.toFixed(1)}
            </div>
            {renderStars(averageRating)}
            <p className="mt-1 text-xs text-[var(--color-text-muted)]" style={font}>
              {totalCount} {totalCount === 1 ? 'review' : 'reviews'}
            </p>
          </div>
        </div>
      )}

      {/* Review list */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
          >
            <div className="flex items-start gap-3">
              {review.reviewer.avatarUrl ? (
                <img
                  src={review.reviewer.avatarUrl}
                  alt={review.reviewer.username}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-bg-elevated)] text-xs font-bold text-[var(--color-text-muted)]">
                  {(review.reviewer.displayName || review.reviewer.username).charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--color-text-primary)]" style={font}>
                    {review.reviewer.displayName || review.reviewer.username}
                  </span>
                  {renderStars(review.overallRating)}
                </div>
                <h4 className="mt-1 text-sm font-semibold text-[var(--color-text-primary)]" style={font}>
                  {review.title}
                </h4>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
                  {review.content}
                </p>
                <p className="mt-2 text-xs text-[var(--color-text-muted)]" style={font}>
                  {new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  {review.collaborationType && ` \u00b7 ${review.collaborationType}`}
                </p>
              </div>
            </div>

            {/* Company response */}
            {review.responseContent && (
              <div className="ml-11 mt-3 rounded-[var(--radius-md)] border-l-2 border-[var(--color-brand-indigo)] bg-[var(--color-bg-elevated)] p-3">
                <p className="text-xs font-medium text-[var(--color-brand-indigo)]" style={font}>Company Response</p>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]" style={font}>
                  {review.responseContent}
                </p>
              </div>
            )}

            {/* Respond button (owner only) */}
            {isOwner && !review.responseContent && (
              <div className="ml-11 mt-2">
                {respondingTo === review.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Write your response (20-1000 characters)..."
                      rows={3}
                      maxLength={1000}
                      className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
                      style={font}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleRespond(review.id)}
                        disabled={isPending || responseText.trim().length < 20}
                        className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                        style={font}
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRespondingTo(null); setResponseText('') }}
                        className="rounded-[var(--radius-md)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]"
                        style={font}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRespondingTo(review.id)}
                    className="text-xs font-medium text-[var(--color-brand-indigo)] hover:underline"
                    style={font}
                  >
                    Respond
                  </button>
                )}
              </div>
            )}

            {/* D6.QW.1 - Report-this-review (any logged-in user, not the
                review's own author). Hidden for the reviewer themselves -
                the server still enforces this with a FORBIDDEN response,
                but hiding the button avoids a confusing dead-end click. */}
            {currentUserId && currentUserId !== review.reviewerId && (
            <div className="ml-11 mt-2">
              {reportingId === review.id ? (
                <div className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3">
                  <p className="text-xs font-medium text-[var(--color-text-primary)]" style={font}>
                    Report this review
                  </p>
                  <select
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value as ReportReason)}
                    className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
                    style={font}
                    aria-label="Reason"
                  >
                    {REPORT_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <textarea
                    value={reportDescription}
                    onChange={(e) => setReportDescription(e.target.value)}
                    placeholder="Optional details (max 500 chars)…"
                    rows={2}
                    maxLength={500}
                    className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
                    style={font}
                    aria-label="Description"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleReportSubmit(review.id)}
                      disabled={isPending}
                      className="rounded-[var(--radius-md)] bg-[var(--color-feedback-error,#dc2626)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      style={font}
                    >
                      {isPending ? 'Submitting…' : 'Submit report'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReportingId(null)
                        setReportDescription('')
                        setReportReason('spam')
                      }}
                      className="rounded-[var(--radius-md)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]"
                      style={font}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setReportingId(review.id)}
                  className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-secondary)]"
                  style={font}
                  aria-label={`Report review by ${review.reviewer.displayName || review.reviewer.username}`}
                >
                  <Flag className="h-3 w-3" aria-hidden="true" />
                  Report
                </button>
              )}
            </div>
            )}
          </div>
        ))}
      </div>

      {/* D6.QW.1 - transient feedback for report submissions. */}
      {reportFeedback && (
        <div
          role="status"
          aria-live="polite"
          className="mt-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-secondary)]"
          style={font}
        >
          {reportFeedback}
        </div>
      )}

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isPending}
            className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            style={font}
          >
            {isPending ? 'Loading...' : 'Load more reviews'}
          </button>
        </div>
      )}
    </div>
  )
}
